import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  X, Loader2, CheckCircle, XCircle, GitMerge, Plus, MapPin, Calendar, User, Camera, ArrowLeft, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const SubmissionReviewPanel = ({ onClose, onReviewComplete }) => {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // 'approve_new', 'approve_merge', 'reject'
  const [processing, setProcessing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [existingSpecies, setExistingSpecies] = useState([]);
  const [searchMerge, setSearchMerge] = useState("");
  const [taxonomyData, setTaxonomyData] = useState(null);
  
  // New species form data (for approve_new)
  const [newSpeciesData, setNewSpeciesData] = useState({
    common_names: [],
    scientific_name: "",
    simple_category: "",
    description: "",
    habitat: "",
    conservation_status: "",
    seasonality: [],
    taxonomy: {
      kingdom: "Animalia",
      phylum: "",
      class_name: "",
      order: "",
      family: "",
      genus: "",
      species: ""
    }
  });
  
  const [reviewNotes, setReviewNotes] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeTargetSpecies, setMergeTargetSpecies] = useState(null);
  const [customTaxonomy, setCustomTaxonomy] = useState({
    phylum: false,
    class_name: false,
    order: false,
    family: false,
  });

  // Build reverse lookup for taxonomy
  const taxonomyReverseLookup = useMemo(() => {
    if (!taxonomyData?.tree) return {};
    
    const lookup = {
      families: {},
      orders: {},
      classes: {},
    };
    
    Object.entries(taxonomyData.tree).forEach(([phylum, phylumData]) => {
      if (phylumData.classes) {
        Object.entries(phylumData.classes).forEach(([className, classData]) => {
          lookup.classes[className] = { phylum };
          
          if (classData.orders) {
            Object.entries(classData.orders).forEach(([order, orderData]) => {
              lookup.orders[order] = { class_name: className, phylum };
              
              if (orderData.families) {
                Object.keys(orderData.families).forEach(family => {
                  lookup.families[family] = { order, class_name: className, phylum };
                });
              }
            });
          }
        });
      }
    });
    
    return lookup;
  }, [taxonomyData]);

  // Taxonomy getter functions
  const getPhyla = () => {
    if (!taxonomyData?.tree) return [];
    return Object.keys(taxonomyData.tree).sort();
  };

  const getFilteredClasses = () => {
    if (newSpeciesData.taxonomy.phylum && taxonomyData?.tree) {
      const phylumData = taxonomyData.tree[newSpeciesData.taxonomy.phylum];
      return phylumData?.classes ? Object.keys(phylumData.classes).sort() : [];
    }
    return Object.keys(taxonomyReverseLookup.classes || {}).sort();
  };

  const getFilteredOrders = () => {
    if (newSpeciesData.taxonomy.class_name) {
      const filteredOrders = Object.entries(taxonomyReverseLookup.orders || {})
        .filter(([_, info]) => info.class_name === newSpeciesData.taxonomy.class_name)
        .map(([order]) => order);
      return filteredOrders.sort();
    }
    if (newSpeciesData.taxonomy.phylum) {
      const filteredOrders = Object.entries(taxonomyReverseLookup.orders || {})
        .filter(([_, info]) => info.phylum === newSpeciesData.taxonomy.phylum)
        .map(([order]) => order);
      return filteredOrders.sort();
    }
    return Object.keys(taxonomyReverseLookup.orders || {}).sort();
  };

  const getFilteredFamilies = () => {
    if (newSpeciesData.taxonomy.order) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.order === newSpeciesData.taxonomy.order)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    if (newSpeciesData.taxonomy.class_name) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.class_name === newSpeciesData.taxonomy.class_name)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    if (newSpeciesData.taxonomy.phylum) {
      const filteredFamilies = Object.entries(taxonomyReverseLookup.families || {})
        .filter(([_, info]) => info.phylum === newSpeciesData.taxonomy.phylum)
        .map(([family]) => family);
      return filteredFamilies.sort();
    }
    return Object.keys(taxonomyReverseLookup.families || {}).sort();
  };

  // Taxonomy handlers
  const handlePhylumSelect = (phylum) => {
    setNewSpeciesData({
      ...newSpeciesData,
      taxonomy: { ...newSpeciesData.taxonomy, phylum, class_name: "", order: "", family: "" }
    });
  };

  const handleClassSelect = (className) => {
    const parentInfo = taxonomyReverseLookup.classes[className];
    if (parentInfo) {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: {
          ...newSpeciesData.taxonomy,
          class_name: className,
          phylum: parentInfo.phylum,
          order: "",
          family: ""
        }
      });
    } else {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: { ...newSpeciesData.taxonomy, class_name: className, order: "", family: "" }
      });
    }
  };

  const handleOrderSelect = (order) => {
    const parentInfo = taxonomyReverseLookup.orders[order];
    if (parentInfo) {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: {
          ...newSpeciesData.taxonomy,
          order,
          class_name: parentInfo.class_name,
          phylum: parentInfo.phylum,
          family: ""
        }
      });
    } else {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: { ...newSpeciesData.taxonomy, order, family: "" }
      });
    }
  };

  const handleFamilySelect = (family) => {
    const parentInfo = taxonomyReverseLookup.families[family];
    if (parentInfo) {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: {
          ...newSpeciesData.taxonomy,
          family,
          order: parentInfo.order,
          class_name: parentInfo.class_name,
          phylum: parentInfo.phylum
        }
      });
    } else {
      setNewSpeciesData({
        ...newSpeciesData,
        taxonomy: { ...newSpeciesData.taxonomy, family }
      });
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchCategories();
    fetchTaxonomyData();
  }, []);

  const fetchTaxonomyData = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/taxonomy-tree`);
      setTaxonomyData(response.data);
    } catch (error) {
      console.error("Error loading taxonomy:", error);
    }
  };

  useEffect(() => {
    if (searchMerge.length >= 2) {
      searchSpecies(searchMerge);
    } else {
      setExistingSpecies([]);
    }
  }, [searchMerge]);

  // When a merge target is pre-set (e.g. from a submission's
  // merge_with_species_id), fetch and display that species so the admin can
  // confirm with a single click.
  useEffect(() => {
    if (!mergeTargetId) {
      setMergeTargetSpecies(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/marine-database/${mergeTargetId}`);
        if (!cancelled) setMergeTargetSpecies(res.data || null);
      } catch (_) {
        if (!cancelled) setMergeTargetSpecies(null);
      }
    })();
    return () => { cancelled = true; };
  }, [mergeTargetId]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const response = await axios.get(`${API}/marine-database/submissions/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const searchSpecies = async (query) => {
    try {
      const response = await axios.get(`${API}/marine-database/search?q=${encodeURIComponent(query)}&limit=10`);
      setExistingSpecies(response.data);
    } catch (error) {
      console.error("Error searching species:", error);
    }
  };

  const selectSubmission = (submission) => {
    setSelectedSubmission(submission);
    setReviewNotes("");
    setSearchMerge("");

    // Determine common_names: prefer the explicit array, fall back to species_name.
    const submittedCommonNames = (Array.isArray(submission.common_names) && submission.common_names.length > 0)
      ? submission.common_names
      : (submission.species_name ? [submission.species_name] : []);

    // Auto-derive scientific name from taxonomy if not present
    const tax = submission.taxonomy || {};
    const derivedSci = (tax.genus && tax.species) ? `${tax.genus} ${tax.species}` : "";

    // Pre-fill new species data from the submission
    setNewSpeciesData({
      common_names: submittedCommonNames,
      scientific_name: submission.scientific_name || derivedSci || "",
      simple_category: submission.suggested_category || "",
      description: "",
      habitat: "",
      conservation_status: "",
      seasonality: [],
      taxonomy: submission.taxonomy || {
        kingdom: "Animalia",
        phylum: "",
        class_name: "",
        order: "",
        family: "",
        genus: "",
        species: ""
      }
    });

    // Restore custom-input toggles from the submission so the reviewer sees
    // exactly which fields the user typed manually vs. picked from dropdowns.
    if (submission.custom_taxonomy_flags && typeof submission.custom_taxonomy_flags === "object") {
      setCustomTaxonomy({
        phylum: !!submission.custom_taxonomy_flags.phylum,
        class_name: !!submission.custom_taxonomy_flags.class_name,
        order: !!submission.custom_taxonomy_flags.order,
        family: !!submission.custom_taxonomy_flags.family,
        ...(submission.custom_taxonomy_flags.genus !== undefined && { genus: !!submission.custom_taxonomy_flags.genus }),
      });
    } else {
      setCustomTaxonomy({ phylum: false, class_name: false, order: false, family: false });
    }

    // If the user picked an existing species in the submission helper, default
    // to "merge with that one" so the admin can just click Accept.
    if (submission.merge_with_species_id) {
      setMergeTargetId(submission.merge_with_species_id);
      setReviewAction("approve_merge");
    } else {
      setMergeTargetId("");
      setReviewAction(null);
    }
  };

  const handleReview = async () => {
    if (!reviewAction) {
      toast.error("Please select an action");
      return;
    }

    if (reviewAction === "approve_merge" && !mergeTargetId) {
      toast.error("Please select a species to merge with");
      return;
    }

    if (reviewAction === "approve_new" && (!newSpeciesData.common_names.length || !newSpeciesData.simple_category)) {
      toast.error("Please fill in required fields (name and category)");
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      
      const payload = {
        action: reviewAction,
        notes: reviewNotes || null
      };

      if (reviewAction === "approve_merge") {
        payload.merge_with_species_id = mergeTargetId;
      }

      if (reviewAction === "approve_new") {
        payload.species_data = newSpeciesData;
      }

      await axios.post(
        `${API}/marine-database/submissions/${selectedSubmission.id}/review`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Submission ${reviewAction.replace("_", " ")}d!`);
      setSelectedSubmission(null);
      fetchSubmissions();
      onReviewComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process review");
    } finally {
      setProcessing(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  };

  const MONTHS = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div 
        className="bg-white sm:rounded-2xl w-full sm:max-w-5xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            {/* Mobile back button when viewing a submission */}
            {selectedSubmission && (
              <button
                onClick={() => setSelectedSubmission(null)}
                className="sm:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700"
                data-testid="review-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Review Submissions</h2>
              <p className="text-sm text-gray-500">{submissions.length} pending review</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden min-h-0">
          {/* Submissions List — hidden on mobile when a submission is selected */}
          <div className={`${selectedSubmission ? 'hidden sm:block' : 'block'} sm:w-1/3 border-b sm:border-b-0 sm:border-r overflow-y-auto flex-shrink-0`}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>All caught up!</p>
                <p className="text-sm">No pending submissions</p>
              </div>
            ) : (
              <div className="divide-y">
                {submissions.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => selectSubmission(sub)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedSubmission?.id === sub.id ? "bg-cyan-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{sub.species_name}</span>
                      {sub.submission_type === "revision" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold uppercase" data-testid="revision-badge">
                          Revision
                        </span>
                      )}
                    </div>
                    {sub.submission_type === "revision" && sub.revision_description && (
                      <div className="text-xs text-amber-600 mt-1 line-clamp-2 flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                        {sub.revision_description}
                      </div>
                    )}
                    <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {sub.dive_site_name}
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <User className="w-3 h-3" />
                      {sub.submitted_by?.user_name}
                      <span>•</span>
                      <Calendar className="w-3 h-3" />
                      {new Date(sub.submitted_at).toLocaleDateString()}
                    </div>
                    {sub.image_urls?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-cyan-600">
                        <Camera className="w-3 h-3" />
                        {sub.image_urls.length} photo{sub.image_urls.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Review Panel — on mobile, shown only when a submission is selected */}
          <div className={`${!selectedSubmission ? 'hidden sm:flex' : 'flex'} flex-1 overflow-y-auto p-4 flex-col`}>
            {!selectedSubmission ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                Select a submission to review
              </div>
            ) : (
              <div className="space-y-6">
                {/* Submission Details */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-semibold text-lg">{selectedSubmission.species_name}</h3>
                    {selectedSubmission.submission_type === "revision" && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold uppercase">
                        Revision Request
                      </span>
                    )}
                  </div>
                  
                  {/* Revision info banner */}
                  {selectedSubmission.submission_type === "revision" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="revision-info-banner">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">What needs changing:</p>
                          <p className="text-sm text-amber-700 mt-1">{selectedSubmission.revision_description}</p>
                          {selectedSubmission.revision_field_affected && (
                            <p className="text-xs text-amber-600 mt-1">Field: {selectedSubmission.revision_field_affected}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <div className="font-medium">{selectedSubmission.dive_site_name}</div>
                      <div className="text-gray-500">{selectedSubmission.country}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Submitted by:</span>
                      <div className="font-medium">{selectedSubmission.submitted_by?.user_name}</div>
                      <div className="text-gray-500">
                        {new Date(selectedSubmission.submitted_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {selectedSubmission.suggested_category && (
                    <div className="mt-3">
                      <span className="text-gray-500 text-sm">Suggested category: </span>
                      <Badge variant="outline">
                        {categories.find(c => c.id === selectedSubmission.suggested_category)?.icon}{" "}
                        {categories.find(c => c.id === selectedSubmission.suggested_category)?.name || selectedSubmission.suggested_category}
                      </Badge>
                    </div>
                  )}

                  {/* Photos */}
                  {selectedSubmission.image_urls?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm text-gray-500 mb-2">Photos:</div>
                      <div className="flex gap-2 flex-wrap">
                        {selectedSubmission.image_urls.map((url, idx) => (
                          <img
                            key={idx}
                            src={getImageUrl(url)}
                            alt="Submission"
                            className="w-24 h-24 object-cover rounded-lg border"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Selection */}
                <div>
                  <Label className="mb-2 block">What would you like to do?</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={() => setReviewAction("approve_new")}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        reviewAction === "approve_new"
                          ? "border-green-500 bg-green-50"
                          : "border-gray-200 hover:border-green-300"
                      }`}
                    >
                      <Plus className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <div className="font-medium text-green-700">Add as New</div>
                      <div className="text-xs text-gray-500">Create new species</div>
                    </button>

                    <button
                      onClick={() => setReviewAction("approve_merge")}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        reviewAction === "approve_merge"
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <GitMerge className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <div className="font-medium text-blue-700">Merge</div>
                      <div className="text-xs text-gray-500">Add to existing species</div>
                    </button>

                    <button
                      onClick={() => setReviewAction("reject")}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        reviewAction === "reject"
                          ? "border-red-500 bg-red-50"
                          : "border-gray-200 hover:border-red-300"
                      }`}
                    >
                      <XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                      <div className="font-medium text-red-700">Reject</div>
                      <div className="text-xs text-gray-500">Invalid submission</div>
                    </button>
                  </div>
                </div>

                {/* Action-specific forms */}
                {reviewAction === "approve_new" && (
                  <div className="space-y-4 bg-green-50 rounded-xl p-4">
                    <h4 className="font-medium text-green-800">New Species Details</h4>

                    <div>
                      <Label>Common Name(s) *</Label>
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1">
                          {newSpeciesData.common_names.map((name, idx) => (
                            <span
                              key={`${name}-${idx}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs"
                              data-testid={`review-common-name-chip-${idx}`}
                            >
                              {idx === 0 && <span className="font-semibold">★</span>}
                              {name}
                              <button
                                type="button"
                                onClick={() => setNewSpeciesData({
                                  ...newSpeciesData,
                                  common_names: newSpeciesData.common_names.filter((_, i) => i !== idx)
                                })}
                                className="hover:text-red-600"
                                aria-label={`Remove ${name}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-1">
                          <Input
                            placeholder={'Add another name (e.g., "Anemonefish")'}
                            className="h-9 text-xs"
                            data-testid="review-add-common-name-input"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const v = e.target.value.trim();
                                if (v && !newSpeciesData.common_names.includes(v)) {
                                  setNewSpeciesData({
                                    ...newSpeciesData,
                                    common_names: [...newSpeciesData.common_names, v]
                                  });
                                  e.target.value = "";
                                }
                              }
                            }}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="review-add-common-name-btn"
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling;
                              const v = (input?.value || "").trim();
                              if (v && !newSpeciesData.common_names.includes(v)) {
                                setNewSpeciesData({
                                  ...newSpeciesData,
                                  common_names: [...newSpeciesData.common_names, v]
                                });
                                input.value = "";
                              }
                            }}
                          >+</Button>
                        </div>
                        <p className="text-xs text-gray-500">First name is primary; rest become "also known as".</p>
                      </div>
                    </div>

                    <div>
                      <Label>Scientific Name</Label>
                      <Input
                        value={newSpeciesData.scientific_name}
                        onChange={(e) => setNewSpeciesData({ ...newSpeciesData, scientific_name: e.target.value })}
                        placeholder="e.g., Rhincodon typus"
                        data-testid="review-scientific-name-input"
                      />
                      {newSpeciesData.taxonomy?.genus && newSpeciesData.taxonomy?.species && (
                        <button
                          type="button"
                          className="text-xs text-green-700 hover:underline mt-1"
                          onClick={() => setNewSpeciesData({
                            ...newSpeciesData,
                            scientific_name: `${newSpeciesData.taxonomy.genus} ${newSpeciesData.taxonomy.species}`
                          })}
                        >
                          ↻ Auto-fill from taxonomy: {newSpeciesData.taxonomy.genus} {newSpeciesData.taxonomy.species}
                        </button>
                      )}
                    </div>

                    <div>
                      <Label>Category *</Label>
                      <Select
                        value={newSpeciesData.simple_category}
                        onValueChange={(value) => setNewSpeciesData({ ...newSpeciesData, simple_category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newSpeciesData.description}
                        onChange={(e) => setNewSpeciesData({ ...newSpeciesData, description: e.target.value })}
                        placeholder="Brief description of the species..."
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Habitat</Label>
                        <Input
                          value={newSpeciesData.habitat}
                          onChange={(e) => setNewSpeciesData({ ...newSpeciesData, habitat: e.target.value })}
                          placeholder="e.g., Coral reefs, open ocean"
                        />
                      </div>
                      <div>
                        <Label>Conservation Status</Label>
                        <Select
                          value={newSpeciesData.conservation_status}
                          onValueChange={(value) => setNewSpeciesData({ ...newSpeciesData, conservation_status: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="LC">Least Concern</SelectItem>
                            <SelectItem value="NT">Near Threatened</SelectItem>
                            <SelectItem value="VU">Vulnerable</SelectItem>
                            <SelectItem value="EN">Endangered</SelectItem>
                            <SelectItem value="CR">Critically Endangered</SelectItem>
                            <SelectItem value="DD">Data Deficient</SelectItem>
                            <SelectItem value="NE">Not Evaluated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Taxonomy Section - Smart Filtering with Custom Toggle */}
                    <div className="border-t border-green-200 pt-3 mt-3">
                      <Label className="text-sm font-semibold text-green-800 mb-2 block">
                        Taxonomy (Optional) - Select any level, others auto-filter
                      </Label>
                      
                      {/* Phylum */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500">Phylum</Label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={customTaxonomy.phylum}
                                onChange={(e) => setCustomTaxonomy({ ...customTaxonomy, phylum: e.target.checked })}
                                className="w-3 h-3"
                              />
                              <span className="text-[10px] text-gray-400">Custom</span>
                            </label>
                          </div>
                          {customTaxonomy.phylum ? (
                            <Input
                              value={newSpeciesData.taxonomy?.phylum || ""}
                              onChange={(e) => setNewSpeciesData({
                                ...newSpeciesData,
                                taxonomy: { ...newSpeciesData.taxonomy, phylum: e.target.value, class_name: "", order: "", family: "" }
                              })}
                              placeholder="Type phylum..."
                              className="h-8 text-sm"
                            />
                          ) : (
                            <Select
                              value={newSpeciesData.taxonomy?.phylum || ""}
                              onValueChange={handlePhylumSelect}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select phylum..." />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {getPhyla().map((p) => (
                                  <SelectItem key={p} value={p}>{p}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500">
                              Class {!customTaxonomy.class_name && newSpeciesData.taxonomy?.phylum && <span className="text-green-600">(filtered)</span>}
                            </Label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={customTaxonomy.class_name}
                                onChange={(e) => setCustomTaxonomy({ ...customTaxonomy, class_name: e.target.checked })}
                                className="w-3 h-3"
                              />
                              <span className="text-[10px] text-gray-400">Custom</span>
                            </label>
                          </div>
                          {customTaxonomy.class_name ? (
                            <Input
                              value={newSpeciesData.taxonomy?.class_name || ""}
                              onChange={(e) => setNewSpeciesData({
                                ...newSpeciesData,
                                taxonomy: { ...newSpeciesData.taxonomy, class_name: e.target.value, order: "", family: "" }
                              })}
                              placeholder="Type class..."
                              className="h-8 text-sm"
                            />
                          ) : (
                            <Select
                              value={newSpeciesData.taxonomy?.class_name || ""}
                              onValueChange={handleClassSelect}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select class..." />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {getFilteredClasses().map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500">
                              Order {!customTaxonomy.order && newSpeciesData.taxonomy?.class_name && <span className="text-green-600">(filtered)</span>}
                            </Label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={customTaxonomy.order}
                                onChange={(e) => setCustomTaxonomy({ ...customTaxonomy, order: e.target.checked })}
                                className="w-3 h-3"
                              />
                              <span className="text-[10px] text-gray-400">Custom</span>
                            </label>
                          </div>
                          {customTaxonomy.order ? (
                            <Input
                              value={newSpeciesData.taxonomy?.order || ""}
                              onChange={(e) => setNewSpeciesData({
                                ...newSpeciesData,
                                taxonomy: { ...newSpeciesData.taxonomy, order: e.target.value, family: "" }
                              })}
                              placeholder="Type order..."
                              className="h-8 text-sm"
                            />
                          ) : (
                            <Select
                              value={newSpeciesData.taxonomy?.order || ""}
                              onValueChange={handleOrderSelect}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select order..." />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {getFilteredOrders().map((o) => (
                                  <SelectItem key={o} value={o}>{o}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500">
                              Family {!customTaxonomy.family && (newSpeciesData.taxonomy?.order || newSpeciesData.taxonomy?.class_name) && <span className="text-green-600">(filtered)</span>}
                            </Label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={customTaxonomy.family}
                                onChange={(e) => setCustomTaxonomy({ ...customTaxonomy, family: e.target.checked })}
                                className="w-3 h-3"
                              />
                              <span className="text-[10px] text-gray-400">Custom</span>
                            </label>
                          </div>
                          {customTaxonomy.family ? (
                            <Input
                              value={newSpeciesData.taxonomy?.family || ""}
                              onChange={(e) => setNewSpeciesData({
                                ...newSpeciesData,
                                taxonomy: { ...newSpeciesData.taxonomy, family: e.target.value }
                              })}
                              placeholder="Type family..."
                              className="h-8 text-sm"
                            />
                          ) : (
                            <Select
                              value={newSpeciesData.taxonomy?.family || ""}
                              onValueChange={handleFamilySelect}
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select family..." />
                              </SelectTrigger>
                              <SelectContent className="bg-white max-h-48">
                                {getFilteredFamilies().map((f) => (
                                  <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-gray-500">Genus</Label>
                          <Input
                            value={newSpeciesData.taxonomy?.genus || ""}
                            onChange={(e) => setNewSpeciesData({ 
                              ...newSpeciesData, 
                              taxonomy: { ...newSpeciesData.taxonomy, genus: e.target.value }
                            })}
                            placeholder="e.g., Amphiprion"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Species</Label>
                          <Input
                            value={newSpeciesData.taxonomy?.species || ""}
                            onChange={(e) => setNewSpeciesData({ 
                              ...newSpeciesData, 
                              taxonomy: { ...newSpeciesData.taxonomy, species: e.target.value }
                            })}
                            placeholder="e.g., ocellaris"
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Clear taxonomy button */}
                      {(newSpeciesData.taxonomy?.phylum || newSpeciesData.taxonomy?.class_name) && (
                        <button
                          type="button"
                          onClick={() => setNewSpeciesData({
                            ...newSpeciesData,
                            taxonomy: { kingdom: "Animalia", phylum: "", class_name: "", order: "", family: "", genus: "", species: "" }
                          })}
                          className="text-xs text-gray-500 hover:text-red-500 underline mt-2"
                        >
                          Clear taxonomy
                        </button>
                      )}
                    </div>

                    <div>
                      <Label>Seasonality (months visible)</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {MONTHS.map((month) => (
                          <button
                            key={month}
                            type="button"
                            onClick={() => {
                              const current = newSpeciesData.seasonality || [];
                              if (current.includes(month)) {
                                setNewSpeciesData({ ...newSpeciesData, seasonality: current.filter(m => m !== month) });
                              } else {
                                setNewSpeciesData({ ...newSpeciesData, seasonality: [...current, month] });
                              }
                            }}
                            className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                              newSpeciesData.seasonality?.includes(month)
                                ? "bg-green-200 border-green-400 text-green-800"
                                : "bg-white border-gray-200 text-gray-600 hover:border-green-300"
                            }`}
                          >
                            {month.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {reviewAction === "approve_merge" && (
                  <div className="space-y-4 bg-blue-50 rounded-xl p-4">
                    <h4 className="font-medium text-blue-800">Merge with Existing Species</h4>

                    {/* Pre-selected merge target from submission helper */}
                    {mergeTargetSpecies && (
                      <div className="bg-white border-2 border-emerald-300 rounded-lg p-3" data-testid="prefilled-merge-target">
                        <div className="text-xs text-emerald-700 font-medium mb-2">
                          ✓ User picked this from the submission helper. Click Accept to merge.
                        </div>
                        <div className="flex items-center gap-3">
                          {mergeTargetSpecies.images?.[0] ? (
                            <img
                              src={getImageUrl(mergeTargetSpecies.images[0])}
                              alt={mergeTargetSpecies.common_names?.[0] || "species"}
                              className="w-16 h-16 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Camera className="w-5 h-5 text-blue-400" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{mergeTargetSpecies.common_names?.[0]}</div>
                            <div className="text-sm text-gray-500 italic truncate">{mergeTargetSpecies.scientific_name}</div>
                            {mergeTargetSpecies.simple_category && (
                              <Badge variant="outline" className="text-[10px] mt-1">{mergeTargetSpecies.simple_category}</Badge>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setMergeTargetId(""); setMergeTargetSpecies(null); }}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    )}

                    {(!mergeTargetSpecies || searchMerge) && (
                      <div>
                        <Label>Search for species</Label>
                        <Input
                          value={searchMerge}
                          onChange={(e) => setSearchMerge(e.target.value)}
                          placeholder="Type to search..."
                          data-testid="merge-search-input"
                        />
                      </div>
                    )}

                    {existingSpecies.length > 0 && (
                      <div className="space-y-2">
                        {existingSpecies.map((species) => {
                          const thumb = species.images?.[0];
                          return (
                            <button
                              key={species.id}
                              onClick={() => setMergeTargetId(species.id)}
                              className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center gap-3 ${
                                mergeTargetId === species.id
                                  ? "border-blue-500 bg-blue-100"
                                  : "border-gray-200 bg-white hover:border-blue-300"
                              }`}
                              data-testid={`merge-candidate-${species.id}`}
                            >
                              {thumb ? (
                                <img
                                  src={getImageUrl(thumb)}
                                  alt={species.common_names?.[0] || "species"}
                                  className="w-12 h-12 rounded-md object-cover bg-gray-100 flex-shrink-0"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Camera className="w-4 h-4 text-blue-400" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{species.common_names?.[0]}</div>
                                <div className="text-sm text-gray-500 italic truncate">{species.scientific_name}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {mergeTargetId && (
                      <div className="text-sm text-blue-700">
                        This sighting will be added to the selected species&apos; location data.
                      </div>
                    )}
                  </div>
                )}

                {reviewAction === "reject" && (
                  <div className="bg-red-50 rounded-xl p-4">
                    <h4 className="font-medium text-red-800 mb-2">Rejection Reason</h4>
                    <p className="text-sm text-red-600 mb-3">
                      Please provide a reason so the submitter knows why it was rejected.
                    </p>
                  </div>
                )}

                {/* Notes */}
                {reviewAction && (
                  <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder={reviewAction === "reject" ? "Reason for rejection..." : "Any additional notes..."}
                      rows={2}
                    />
                  </div>
                )}

                {/* Submit Button */}
                {reviewAction && (
                  <Button
                    onClick={handleReview}
                    disabled={processing}
                    className={`w-full ${
                      reviewAction === "reject"
                        ? "bg-red-500 hover:bg-red-600"
                        : reviewAction === "approve_merge"
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-green-500 hover:bg-green-600"
                    }`}
                  >
                    {processing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                    ) : reviewAction === "approve_new" ? (
                      <><CheckCircle className="w-4 h-4 mr-2" /> Approve as New Species</>
                    ) : reviewAction === "approve_merge" ? (
                      <><GitMerge className="w-4 h-4 mr-2" /> Merge with Selected</>
                    ) : (
                      <><XCircle className="w-4 h-4 mr-2" /> Reject Submission</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionReviewPanel;
